import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import {
  CREDIT_TOP_UP_CREDITS_PER_USD,
  CREDIT_TOP_UP_MAX_USD,
  CREDIT_TOP_UP_MIN_USD,
  CREDIT_TOP_UP_PRESETS_USD,
  CREDIT_TOP_UP_STEP_USD,
  isValidCreditTopUpUsdAmount,
  toCheckoutReturnUrl,
} from '@workspace/global-utils';
import { Button } from '@workspace/ui/components/button';
import { toast } from '@workspace/ui/components/sonner';
import { cn } from '@workspace/ui/lib/utils';
import { authenticatedNextFetch } from '@/api/api-service';
import Modal from '@/components/core/Modal';
import { AI_CREDITS_QUERY_KEY } from '@/hooks/billing/use-ai-credits';
import { creditTopUpModalOpenAtom } from '@/lib/credit-guard';

function getAmountError(value: string): string | null {
  if (value === '') return 'Enter an amount';
  const amount = Number(value);
  if (!Number.isInteger(amount)) return 'Enter a whole dollar amount';
  if (amount < CREDIT_TOP_UP_MIN_USD) return `The minimum is $${CREDIT_TOP_UP_MIN_USD}`;
  if (amount > CREDIT_TOP_UP_MAX_USD) {
    return `The maximum is $${CREDIT_TOP_UP_MAX_USD.toLocaleString()}`;
  }
  if (!isValidCreditTopUpUsdAmount(amount)) return `Use $${CREDIT_TOP_UP_STEP_USD} steps`;
  return null;
}

export function TopUpCreditsModal() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useAtom(creditTopUpModalOpenAtom);
  const [amount, setAmount] = useState(String(CREDIT_TOP_UP_MIN_USD));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const amountError = getAmountError(amount);
  const credits = Number(amount) * CREDIT_TOP_UP_CREDITS_PER_USD;
  const remaining = queryClient.getQueryData<{ remaining: number | null }>(
    AI_CREDITS_QUERY_KEY,
  )?.remaining;

  const handleCheckout = async () => {
    if (amountError) return;
    setIsSubmitting(true);
    try {
      const response = await authenticatedNextFetch('/api/billing/credit-topup', {
        method: 'POST',
        body: JSON.stringify({
          usdAmount: Number(amount),
          returnUrl: toCheckoutReturnUrl(window.location.href),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!response.ok || !payload.url) {
        toast.error(payload.error || 'We couldn’t open checkout. Try again in a moment.');
        setIsSubmitting(false);
        return;
      }
      window.location.href = payload.url;
    } catch {
      toast.error('We couldn’t open checkout. Check your connection and try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      title="Top up AI credits"
      titleClassName="normal-case text-lg font-semibold leading-[27px] text-primary"
      description="Choose how much to add. You’ll finish the purchase on Dodo checkout."
      descriptionClassName="text-sm leading-[21px] text-gray-purple"
      className="sm:max-w-110 rounded-[14px] border-purple-30 p-6"
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold leading-[21px] text-primary-darker">
            Amount{' '}
            <span className="font-normal text-gray-purple">
              (${CREDIT_TOP_UP_MIN_USD}–${CREDIT_TOP_UP_MAX_USD.toLocaleString()}, $
              {CREDIT_TOP_UP_STEP_USD} steps)
            </span>
          </span>
          <span
            className={cn(
              'flex h-12.5 items-center gap-1.5 rounded-[10px] border bg-white px-3.5 transition-[border-color,box-shadow] duration-150 focus-within:ring-2',
              amountError
                ? 'border-red-400 focus-within:ring-red-400/20'
                : 'border-purple-30 focus-within:border-primary/40 focus-within:ring-primary/10',
            )}
          >
            <span className="text-sm font-semibold text-gray-purple">$</span>
            <input
              type="number"
              inputMode="numeric"
              min={CREDIT_TOP_UP_MIN_USD}
              max={CREDIT_TOP_UP_MAX_USD}
              step={CREDIT_TOP_UP_STEP_USD}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isSubmitting}
              aria-invalid={Boolean(amountError)}
              data-ph-selector="credits-top-up-amount"
              className="min-w-0 flex-1 bg-transparent text-lg font-semibold text-primary-darker outline-none"
            />
          </span>
          {amountError && <span className="text-sm text-red-600">{amountError}</span>}
        </label>

        <div className="flex flex-wrap gap-2">
          {CREDIT_TOP_UP_PRESETS_USD.map((preset) => {
            const isSelected = Number(amount) === preset;
            return (
              <button
                key={preset}
                type="button"
                onClick={() => setAmount(String(preset))}
                disabled={isSubmitting}
                aria-pressed={isSelected}
                data-ph-selector="credits-top-up-preset"
                className={cn(
                  'rounded-full border px-3 py-1.25 text-sm font-medium leading-[21px] transition-[background-color,border-color,color] duration-150',
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-purple-30 bg-white text-primary-darker hover:bg-purple-50',
                )}
              >
                ${preset}
              </button>
            );
          })}
        </div>

        {!amountError && (
          <div className="flex flex-col gap-1.5 rounded-[10px] border border-purple-30 bg-purple-50 px-3.5 py-3">
            <p className="flex justify-between text-[15px] leading-[22.5px] text-primary-darker">
              You’ll receive
              <span className="font-bold text-primary">{credits.toLocaleString()} credits</span>
            </p>
            {typeof remaining === 'number' && (
              <p className="flex justify-between text-[13px] leading-[19.5px] text-gray-purple">
                New balance
                <span>{(Math.max(remaining, 0) + credits).toLocaleString()} credits</span>
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-[10px] border-purple-30 font-semibold"
            data-ph-selector="credits-top-up-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCheckout}
            disabled={isSubmitting || Boolean(amountError)}
            className="rounded-[10px] font-semibold"
            data-ph-selector="credits-top-up-checkout"
          >
            {isSubmitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Continue to checkout
          </Button>
        </div>
      </div>
    </Modal>
  );
}
